import { Suspense, JSX, useEffect } from "react";
import { useRoutes, Navigate } from "react-router-dom";
import { WithLoggedInAuthInfoProps, withRequiredAuthInfo } from "@propelauth/react";
import { AuthProvider } from "./auth";
import { routes } from "./routes";
import { NavBar } from "./components/NavBar";
import { useAuthSync } from "./hooks/useAuthSync";
import { FusawareToast } from "./toasts/FusawareToast";
import { useGlobalStore } from "./store/GlobalStore";
import { useFusaOrganizationStore } from "./store/FusaOrganizationStore";
import { useWorkspaceStore } from "./store/WorkspaceStore";
import { ScrollToAnchor } from "./components/ScrollToAnchor";

const ProtectedRoute = withRequiredAuthInfo((props: WithLoggedInAuthInfoProps & { children: React.ReactNode }) => {
  if (!props.isLoggedIn) {
    return <Navigate to="/home" replace />;
  }
  return <>{props.children}</>;
});

function AppContent() {
  useAuthSync();

  const fetchOrCreateEnterprise = useGlobalStore(state => state.fetchOrCreateEnterprise);
  const fetchOrCreateUser = useGlobalStore(state => state.fetchOrCreateUser);

  useEffect(() => {
    void fetchOrCreateEnterprise();
    void fetchOrCreateUser();
  }, []);

  const enterprise = useGlobalStore(state => state.enterprise);
  const fetchFusaOrganizationsByEnterpriseId = useFusaOrganizationStore(state => state.fetchFusaOrganizationsByEnterpriseId);
  const fetchWorkspacesByPropelOrgWithAssigned = useWorkspaceStore(state => state.fetchWorkspacesByPropelOrgWithAssigned);

  useEffect(() => {
    if (enterprise) {
      void fetchFusaOrganizationsByEnterpriseId(enterprise.id);
    }
  }, [fetchFusaOrganizationsByEnterpriseId, enterprise]);

  useEffect(() => {
    void fetchWorkspacesByPropelOrgWithAssigned();
  }, [fetchWorkspacesByPropelOrgWithAssigned]);

  const accessToken = useGlobalStore(state => state.accessToken);
  
  const appRoutes = [
    ...routes.map(route => ({
      ...route,
      element: route.path === "/home" ? route.element : <ProtectedRoute>{route.element}</ProtectedRoute>
    })),
    {
      path: "*",
      element: <Navigate to="/home" replace />
    }
  ];

  const element = useRoutes(appRoutes);

  if (!accessToken) {
    return (
      <div className="d-flex justify-content-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<p>Loading...</p>}>
      <NavBar />
      {/* isolate keeps Bootstrap's sticky-top (z-index 1020) inside the page, below the hover-expanded sidebar. */}
      <div
        className='flex-grow-1 d-flex flex-column main-content page-gutter'
        style={{
          minHeight: '100vh',
          isolation: 'isolate'
        }}
      >
        <div className='w-100' style={{ maxHeight: '100vh', height: '100vh' }}>
          {element}
        </div>
      </div>
    </Suspense>
  );
}

function App(): JSX.Element {
  return (
    <AuthProvider>
      <ScrollToAnchor />
      <FusawareToast />
      <AppContent />
    </AuthProvider>
  );
}

export default App;
